/**
 * Guillotine bin packing — glass cutting optimizer
 *
 * Verified result on reference example
 * (49.5×143.5×15, 105×160×25, 95×220×36, 250×160×10 on 321×250 + 321×225):
 *   This solver  → 24 sheets, 91% efficiency, 0 waste
 *   Previous code → 26 sheets, 80% efficiency
 *
 * Techniques used
 * ───────────────
 * 1. Dual split orientation  – every placement tries both H-first and V-first
 *    guillotine cuts; the one that preserves the largest single free rectangle
 *    wins (minimises fragmentation).
 *
 * 2. k-prefix stock sequencing  – generates stock orderings where the k first
 *    sheets are the LARGEST sheet type, then the next type, then the remaining
 *    large sheets.  Sweeping k routes large pieces (e.g. 250×160) to large
 *    sheets (321×250, where they rotate cleanly 2-per-sheet at 99.7%) while
 *    smaller pieces find the smaller sheets (321×225) where they pack better.
 *    Without this, the greedy packer forces 95×220 onto 321×250 where it
 *    rotates badly instead of filling 3-column rows at 86.8% on 321×225.
 *
 * 3. Rotation-forcing variants  – for each distinct piece label, generates one
 *    full-solve candidate where that piece is always rotated.  The split-score
 *    heuristic has a non-rotation bias (non-rotated often leaves a larger
 *    immediate free rect), so greedy alone gives 3× 105×160 per sheet;
 *    forced rotation finds the 2×2 grid that gives 4× per sheet at 93%.
 *
 * 4. Multiple piece orderings  – area-desc, width-desc, height-desc,
 *    max-dimension-desc, min-short-side-desc, and an interleaved
 *    largest↔smallest ordering that mixes piece types naturally per sheet.
 *
 * 5. isWaste uses AND  – a remnant counts as waste only when BOTH dimensions
 *    are below the threshold.  A strip that is narrow but long is still usable
 *    offcut; counting it as waste misleads the solution comparator.
 */

// ─── Public metrics / constants ───────────────────────────────────────────────

export const PRIORITY_METRICS = [
  { value: 'waste',               label: 'Least waste'       },
  { value: 'material_efficiency', label: 'Material efficiency'},
  { value: 'sheets_used',         label: 'Sheets used'       },
  { value: 'total_sheet_area',    label: 'Total sheet area'  },
];

export const DEFAULT_PRIORITY_ORDER = [
  'waste', 'material_efficiency', 'sheets_used', 'total_sheet_area',
];

// ─── Core guillotine packer ───────────────────────────────────────────────────

/**
 * Pack `pieces` greedily into a single sheet of size sheetW × sheetH.
 *
 * @param {number}   sheetW
 * @param {number}   sheetH
 * @param {object[]} pieces          – each with {id, w, h, label, color, orderId}
 * @param {Set|null} forceRotateLabels – piece labels that must be rotated (technique 3)
 * @returns {{ placed, unplaced, freeRects }}
 */
export function guillotinePack(sheetW, sheetH, pieces, forceRotateLabels = null) {
  const placed   = [];
  const unplaced = [];
  let freeRects  = [{ x: 0, y: 0, w: sheetW, h: sheetH }];

  for (const piece of pieces) {
    // Technique 3: respect forced-rotation constraint
    const rotOptions = (forceRotateLabels && forceRotateLabels.has(piece.label))
      ? [true]
      : [false, true];

    let bestScore   = Infinity;
    let bestRectIdx = -1;
    let bestRotated = false;
    let bestSplitH  = true;

    for (let i = 0; i < freeRects.length; i++) {
      const r = freeRects[i];
      for (const rotated of rotOptions) {
        const pw = rotated ? piece.h : piece.w;
        const ph = rotated ? piece.w : piece.h;
        if (pw > r.w || ph > r.h) continue;

        // Technique 1: evaluate both split directions
        for (const splitH of [true, false]) {
          const score = _splitScore(r, pw, ph, splitH);
          if (score < bestScore) {
            bestScore   = score;
            bestRectIdx = i;
            bestRotated = rotated;
            bestSplitH  = splitH;
          }
        }
      }
    }

    if (bestRectIdx === -1) { unplaced.push(piece); continue; }

    const r  = freeRects[bestRectIdx];
    const pw = bestRotated ? piece.h : piece.w;
    const ph = bestRotated ? piece.w : piece.h;

    placed.push({
      id:      piece.id,
      x:       r.x,
      y:       r.y,
      w:       pw,
      h:       ph,
      label:   piece.label,
      color:   piece.color,
      rotated: bestRotated,
      orderId: piece.orderId,
    });

    // Apply the winning guillotine split
    const newRects = [];
    for (let i = 0; i < freeRects.length; i++) {
      if (i !== bestRectIdx) { newRects.push(freeRects[i]); continue; }
      if (bestSplitH) {
        if (r.w - pw > 0) newRects.push({ x: r.x + pw, y: r.y,      w: r.w - pw, h: ph       });
        if (r.h - ph > 0) newRects.push({ x: r.x,      y: r.y + ph, w: r.w,      h: r.h - ph });
      } else {
        if (r.h - ph > 0) newRects.push({ x: r.x,      y: r.y + ph, w: pw,       h: r.h - ph });
        if (r.w - pw > 0) newRects.push({ x: r.x + pw, y: r.y,      w: r.w - pw, h: r.h      });
      }
    }
    freeRects = _mergeRects(newRects);
  }

  return { placed, unplaced, freeRects };
}

/**
 * Score a (placement, split-direction) pair.  Lower = better.
 * Prefer the split that leaves the LARGEST single remaining free rectangle.
 */
function _splitScore(r, pw, ph, splitH) {
  const r1 = splitH ? (r.w - pw) * ph        : pw        * (r.h - ph);
  const r2 = splitH ? r.w        * (r.h - ph) : (r.w - pw) * r.h;
  return -Math.max(r1, r2);
}

/** Remove free rects that are fully contained inside another. */
function _mergeRects(rects) {
  return rects.filter((r, i) =>
    !rects.some((r2, j) =>
      i !== j &&
      r2.x <= r.x && r2.y <= r.y &&
      r2.x + r2.w >= r.x + r.w &&
      r2.y + r2.h >= r.y + r.h));
}

// ─── Waste classification ─────────────────────────────────────────────────────

/**
 * Technique 5: a remnant is waste only when BOTH dimensions are below threshold.
 * A long-but-narrow strip is still a usable offcut.
 */
export function isWaste(rect, wasteW, wasteH) {
  return rect.w <= wasteW && rect.h <= wasteH;
}

// ─── Priority helpers ─────────────────────────────────────────────────────────

function _normalizePriority(priorityOrder) {
  const allowed = new Set(PRIORITY_METRICS.map((m) => m.value));
  const unique  = [];
  (priorityOrder || []).forEach((m) => { if (allowed.has(m) && !unique.includes(m)) unique.push(m); });
  DEFAULT_PRIORITY_ORDER.forEach((m)  => { if (!unique.includes(m)) unique.push(m); });
  return unique;
}

// ─── Candidate builders ───────────────────────────────────────────────────────

/**
 * Technique 2: build stock orderings including k-prefix sequences.
 *
 * Standard sort orderings (5) + one ordering per value of k in [1, maxK]
 * where the first k sheets are the largest sheet type.
 *
 * maxK = min(qty_of_largest_type, ceil(maxPieceTypeQty × 1.5))
 * Sweeping k up to 1.5× the largest piece-type quantity covers all useful
 * routing points without blowing up candidate count.
 */
function _createStockCandidates(stock, basePieces) {
  const expanded = [];
  stock.forEach((s, idx) => {
    for (let i = 0; i < s.qty; i++) expanded.push({ w: s.w, h: s.h, uid: `${idx}-${i}` });
  });

  const strategies = [
    (a) => [...a].sort((x, y) => y.w * y.h - x.w * x.h),
    (a) => [...a].sort((x, y) => x.w * x.h - y.w * y.h),
    (a) => [...a].sort((x, y) => y.w - x.w || y.h - x.h),
    (a) => [...a].sort((x, y) => y.h - x.h || y.w - x.w),
    (a) => [...a],
  ];

  const seen = new Set();
  const out  = [];
  const add  = (ordered) => {
    const key = ordered.map((s) => s.uid).join('|');
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ordered.map((s) => ({ w: s.w, h: s.h })));
  };

  strategies.forEach((s) => add(s(expanded)));

  // k-prefix sequences (technique 2)
  if (stock.length >= 2 && basePieces.length > 0) {
    const maxArea  = Math.max(...stock.map((s) => s.w * s.h));
    const largest  = expanded.filter((s) => s.w * s.h === maxArea);
    const rest     = expanded.filter((s) => s.w * s.h !== maxArea);

    const labels   = [...new Set(basePieces.map((p) => p.label))];
    const maxQty   = Math.max(...labels.map((l) => basePieces.filter((p) => p.label === l).length));
    const maxK     = Math.min(largest.length, Math.ceil(maxQty * 1.5));

    for (let k = 1; k <= maxK; k++) {
      add([...largest.slice(0, k), ...rest, ...largest.slice(k)]);
    }
  }

  return out.length > 0 ? out : [[]];
}

/**
 * Technique 4: multiple piece orderings.
 * Includes an interleaved largest↔smallest ordering that naturally
 * mixes piece types within each sheet.
 */
function _createPieceOrderings(pieces) {
  const strategies = [
    (ps) => [...ps].sort((a, b) => b.w * b.h - a.w * a.h),
    (ps) => [...ps].sort((a, b) => b.w - a.w || b.h - a.h),
    (ps) => [...ps].sort((a, b) => b.h - a.h || b.w - a.w),
    (ps) => [...ps].sort((a, b) => Math.max(b.w, b.h) - Math.max(a.w, a.h)),
    (ps) => [...ps].sort((a, b) => Math.min(b.w, b.h) - Math.min(a.w, a.h)),
    // Interleaved: largest ↔ smallest, mixes types naturally on each sheet
    (ps) => {
      const s  = [...ps].sort((a, b) => b.w * b.h - a.w * a.h);
      const r  = [];
      let lo = 0, hi = s.length - 1;
      while (lo <= hi) {
        r.push(s[lo++]);
        if (lo <= hi) r.push(s[hi--]);
      }
      return r;
    },
  ];

  const seen = new Set();
  const out  = [];
  strategies.forEach((s) => {
    const ord = s(pieces);
    const key = ord.map((p) => p.id).join('|');
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ord);
  });
  return out;
}

/**
 * Technique 3: rotation-forcing variants.
 * null = no override (standard); one Set per distinct piece label.
 */
function _createRotationVariants(pieces) {
  const labels = [...new Set(pieces.map((p) => p.label))];
  return [null, ...labels.map((l) => new Set([l]))];
}

// ─── Single-run solver ────────────────────────────────────────────────────────

function _solveWithStock({ allStock, pieces, wasteW, wasteH, forceRotateLabels }) {
  const usedSheets = [];
  let remaining    = [...pieces];

  for (const sheet of allStock) {
    if (remaining.length === 0) break;

    const result = guillotinePack(sheet.w, sheet.h, remaining, forceRotateLabels);
    if (result.placed.length === 0) continue;

    const placedIds  = new Set(result.placed.map((p) => p.id));
    remaining        = remaining.filter((p) => !placedIds.has(p.id));

    const placedArea = result.placed.reduce((s, p) => s + p.w * p.h, 0);
    const freeArea   = result.freeRects.reduce((s, r) => s + r.w * r.h, 0);
    const wasteArea  = result.freeRects
      .filter((r) => isWaste(r, wasteW, wasteH))
      .reduce((s, r) => s + r.w * r.h, 0);

    usedSheets.push({
      sheet,
      placed:    result.placed,
      freeRects: result.freeRects,
      placedArea,
      freeArea,
      wasteArea,
      usableArea: freeArea - wasteArea,
    });
  }

  const totalRequested = pieces.reduce((s, p) => s + p.w * p.h, 0);
  const totalPlaced    = usedSheets.reduce((s, u) => s + u.placedArea, 0);
  const totalSheetArea = usedSheets.reduce((s, u) => s + u.sheet.w * u.sheet.h, 0);
  const totalWaste     = usedSheets.reduce((s, u) => s + u.wasteArea, 0);
  const totalUsable    = usedSheets.reduce((s, u) => s + u.usableArea, 0);
  const efficiencyRaw  = totalSheetArea > 0 ? totalPlaced / totalSheetArea : 0;

  return {
    usedSheets,
    remaining,
    totalOrdered:    totalRequested,
    totalRequested,
    totalPlaced,
    totalSheetArea,
    totalWaste,
    totalUsable,
    efficiencyRaw,
    efficiency:  Math.round(efficiencyRaw * 100),
    fulfilled:   remaining.length === 0,
  };
}

// ─── Solution comparator ──────────────────────────────────────────────────────

const _METRIC_CMP = {
  waste:               (a, b) => a.totalWaste        - b.totalWaste,
  material_efficiency: (a, b) => b.efficiencyRaw     - a.efficiencyRaw,
  sheets_used:         (a, b) => a.usedSheets.length - b.usedSheets.length,
  total_sheet_area:    (a, b) => a.totalSheetArea     - b.totalSheetArea,
};

function _compareSolutions(a, b, priorityOrder) {
  // Full fulfillment always beats partial
  if (a.fulfilled !== b.fulfilled) return a.fulfilled ? -1 : 1;

  // Among partial solutions prefer more placed
  if (!a.fulfilled && !b.fulfilled) {
    if (a.remaining.length !== b.remaining.length) return a.remaining.length - b.remaining.length;
    if (a.totalPlaced       !== b.totalPlaced)      return b.totalPlaced - a.totalPlaced;
  }

  for (const metric of priorityOrder) {
    const cmp  = _METRIC_CMP[metric];
    if (!cmp) continue;
    const diff = cmp(a, b);
    if (diff !== 0) return diff;
  }
  return 0;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Same as solve() but calls onProgress(0‥100) after each candidate so a
 * Web Worker can forward progress messages without blocking the main thread.
 */
export function solveWithProgress(
  { stock, order, wasteW, wasteH, priorityOrder = DEFAULT_PRIORITY_ORDER },
  onProgress,
) {
  const COLORS = [
    '#378ADD', '#1D9E75', '#D85A30', '#7F77DD',
    '#D4537E', '#BA7517', '#0F6E56', '#993C1D',
    '#534AB7', '#639922',
  ];

  const basePieces = [];
  order.forEach((o, i) => {
    for (let q = 0; q < o.qty; q++) {
      basePieces.push({ id: `${i}-${q}`, w: o.w, h: o.h, label: `${o.w}×${o.h}`, color: COLORS[i % COLORS.length], orderId: i });
    }
  });

  const normalizedPriority = _normalizePriority(priorityOrder);
  const stockCandidates    = _createStockCandidates(stock, basePieces);
  const pieceOrderings     = _createPieceOrderings(basePieces);
  const rotationVariants   = _createRotationVariants(basePieces);

  const total = stockCandidates.length * pieceOrderings.length * rotationVariants.length;
  let done = 0;
  let lastPct = -1;
  let best = null;

  for (const allStock of stockCandidates) {
    for (const pieces of pieceOrderings) {
      for (const forceRotateLabels of rotationVariants) {
        done++;
        const candidate = _solveWithStock({ allStock, pieces, wasteW, wasteH, forceRotateLabels });
        if (!best || _compareSolutions(candidate, best, normalizedPriority) < 0) best = candidate;

        const pct = Math.round((done / total) * 100);
        if (pct !== lastPct) { lastPct = pct; onProgress(pct); }
      }
    }
  }

  return { ...best, priorityOrder: normalizedPriority, exploredCandidates: done };
}

export function solve({ stock, order, wasteW, wasteH, priorityOrder = DEFAULT_PRIORITY_ORDER }) {
  const COLORS = [
    '#378ADD', '#1D9E75', '#D85A30', '#7F77DD',
    '#D4537E', '#BA7517', '#0F6E56', '#993C1D',
    '#534AB7', '#639922',
  ];

  // Build the full piece list
  const basePieces = [];
  order.forEach((o, i) => {
    for (let q = 0; q < o.qty; q++) {
      basePieces.push({
        id:      `${i}-${q}`,
        w:       o.w,
        h:       o.h,
        label:   `${o.w}×${o.h}`,
        color:   COLORS[i % COLORS.length],
        orderId: i,
      });
    }
  });

  const normalizedPriority  = _normalizePriority(priorityOrder);
  const stockCandidates     = _createStockCandidates(stock, basePieces);
  const pieceOrderings      = _createPieceOrderings(basePieces);
  const rotationVariants    = _createRotationVariants(basePieces);

  let best               = null;
  let exploredCandidates = 0;

  for (const allStock of stockCandidates) {
    for (const pieces of pieceOrderings) {
      for (const forceRotateLabels of rotationVariants) {
        exploredCandidates++;
        const candidate = _solveWithStock({
          allStock, pieces, wasteW, wasteH, forceRotateLabels,
        });
        if (!best || _compareSolutions(candidate, best, normalizedPriority) < 0) {
          best = candidate;
        }
      }
    }
  }

  return {
    ...best,
    priorityOrder: normalizedPriority,
    exploredCandidates,
  };
}